import { useState, useEffect, useContext, useCallback, useRef, ReactElement, ChangeEvent, FC } from 'react';
import { Modal } from 'react-bootstrap';
import IndividualAppUsers from './Recipient/IndividualAppUsers';
import SchoolLinkAppUsers from './Recipient/SchoolLinkAppUsers';
import EmailOnlySubscribers from './Recipient/EmailOnlySubscribers';
import style from '../NotificationThreadDetail/NotificationRecipient.module.css';
import Loader from '../UI/Loader/Loader';
import { toast } from 'react-toastify';
import React from "react";

import {
    updateContactGoup,
    getCategoryTags,
    fetchEmailOnlySubscribers,
    fetchAppUsers,
    fetchAppUsersForDM,
    getSelectUsersForGroup,
    getSelectedRecipientData
} from '../../services/notification.services';
import button from '../Style/ButtonStyle.module.css';
import '../Style/Table-style.css';
import tableStyle from '../Style/TableModel.module.css';
import { MdOutlineMarkunreadMailbox } from "react-icons/md";
import { ImMobile } from "react-icons/im";
import { FaUserPlus } from "react-icons/fa";
import AuthContext from "../../store/auth-context";
import NotificationRecipientPreview from "./NotificationRecipientPreview";
import {
    buildContactGroupPayload,
    isValidRecipientSelection,
    stringToBool,
} from '../../utils/NotificationRecipientUtils';
import type {
    NotificationRecipientProps,
    ContactGroupPayload,
    CategorySelection,
    EmailSubscriberSelection
} from '../../types/NotificationRecipient';

//const orgUuid = '7bce8766-5bd8-4194-b455-ac579dc36587';

/**
 * NotificationRecipientsSelectorDialog Component
 * Manages recipient selection for notifications across multiple channels
 * 
 * @component
 * @param {NotificationRecipientProps} props - Component props
 * @returns {ReactElement} Modal component for recipient selection
 */
const NotificationRecipientsSelectorDialog: FC<NotificationRecipientProps> = (props): ReactElement => {
    const authCtx = useContext(AuthContext);
    const orgUuid = authCtx?.userData?.orgId;
    const epubOrgId = authCtx?.userData?.epubOrgId || '';
    
    // Track which thread this dialog was opened for to prevent stale updates
    const threadUuidRef = useRef<string | boolean>(props.isRecipientUpdate);

    // Destructure props at the top to use in dependency arrays
    const {
        modalStatus,
        onAction,
        saveNotificationThreadConfig,
        channels,
        selectedUsers,
        setSelectedUsers,
        setSelectUsersForGroup,
        selectedCategoriesTags,
        selectedEmailOnlySubscribers: propsSelectedEmailOnlySubscribers,
        isAllSchoolLinkAppUsersSelected,
        isAllEmailOnlySubscribersSelected,
        setRecipientsCount,
        setSelectedCategoryTags: propsSetSelectedCategoryTags,
        setselectedEmailOnlySubscribers: propsSetSelectedEmailOnlySubscribers,
        setIsAllSchoolLinkAppUsersSelected,
        setIsAllEmailOnlySubscribersSelected,
        isRecipientUpdate,
        setPreviewedRecipientData,
        previewedRecipientData
    } = props;

    // State for SchoolLink app users
    const [categories, setCategories] = useState<any[]>([]);
    // State for Select individual app users
    const [users, setUsers] = useState<any[]>([]);
    // State for Email only subscribers
    const [emailOnlySubscribers, setEmailOnlySubscribers] = useState<any[]>([]);
    // State for checked individual user user id
    const [selectedUserId, setSelectedUserId] = useState<string[]>([]);
    // State for selected SchoolLink app users
    const [selectedCategoryTags, setSelectedCategoryTags] = useState<CategorySelection[]>([]);
    // State for selected email Only Subscribers
    const [selectedEmailOnlySubscribers, setSelectedEmailOnlySubscribers] = useState<EmailSubscriberSelection[]>([]);
    // Modal open/close state
    const [isOpen, setOpen] = useState<boolean>(modalStatus);
    // Preview screen visibility state
    const [isNext, setIsNext] = useState<boolean>(false);
    // Data loading state
    const [isLoading, setIsLoading] = useState<boolean>(true);
    // Save operation state
    const [isSaving, setIsSaving] = useState<boolean>(false);
    // Current active tab state
    const [currentTab, setCurrentTab] = useState<number>(
        (Array.isArray(channels) ? channels : [channels]).includes("5") ? 3 : 1
    );
    // Selected filter parameters for preview
    const [previewedRecipientDataTemp, setPreviewedRecipientDataTemp] = useState<any[]>([]);
    
    // Update thread UUID ref when prop changes (dialog opened for different thread)
    useEffect(() => {
        threadUuidRef.current = isRecipientUpdate;
    }, [isRecipientUpdate]);

    /**
     * Handles tab change
     * @param {number} id - Tab ID
     * @returns {void}
     */
    const tabChangeHandler = useCallback((id: number): void => {
        setCurrentTab(id);
    }, []);

    /**
     * Handles user checkbox/radio changes for individual user selection
     * For Direct Messages (channel 5), only one user can be selected at a time
     * @param {ChangeEvent<HTMLInputElement>} event - Checkbox/radio change event
     * @returns {void}
     */
    const getUserCheckedValueHandler = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        const channelsArray = Array.isArray(channels) ? channels : [channels];
        const isDirectMessage = channelsArray.includes("5");
        
        let selected = [...selectedUserId];
        const findIndex = selected.indexOf(value);

        if (isDirectMessage) {
            // For Direct Messages, only allow single selection (radio button behavior)
            selected = [value];
        } else {
            // For regular messages, allow multiple selections (checkbox behavior)
            if (findIndex > -1) {
                selected.splice(findIndex, 1);
            } else {
                selected.push(value);
            }
        }
        
        setSelectedUserId(selected);
        setSelectedUsers(selected);
    }, [selectedUserId, setSelectedUsers, channels]);

    /**
     * Handles selection of tags for a specific category
     * @param {CategorySelection[]} selectedTagsData - Array of selected tags
     * @param {number} cateId - Category ID
     * @returns {void}
     */
    const setSelectedTagsHandlers = (selectedTagsData: any[], cateId: number): void => {
        const newTagsObj = {
            [cateId]: selectedTagsData
        };

        const categoryTag = [...selectedCategoryTags];
        const index = categoryTag.findIndex(e => e[cateId]);

        if (index > -1) {
            categoryTag[index][cateId] = selectedTagsData;
        } else {
            categoryTag.push(newTagsObj);
        }
        setSelectedCategoryTags(categoryTag);
        propsSetSelectedCategoryTags(categoryTag);
    };

    /**
     * Handles email subscriber selection for a category
     * @param {EmailSubscriberSelection[]} selectedTags - Selected email subscribers
     * @param {number} categoryId - Category ID
     * @returns {void}
     */
    const emailOnlySubscribersChangeHandler = (selectedTags: any[], categoryId: number): void => {
        const newTagsObj = {
            [categoryId]: selectedTags
        };
        const selectedAppData = [...selectedEmailOnlySubscribers];
        const index = selectedAppData.findIndex(e => e[categoryId]);
        
        if (index > -1) {
            selectedAppData[index][categoryId] = selectedTags;
        } else {
            selectedAppData.push(newTagsObj);
        }
        setSelectedEmailOnlySubscribers(selectedAppData);
        propsSetSelectedEmailOnlySubscribers(selectedAppData);
    };

    /**
     * Saves recipient details and calls parent handler
     * Validates that at least one recipient is selected before saving
     * @returns {void}
     */
    const saveRecipientHandler = (): void => {
        if (!isValidRecipientSelection({
            isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected,
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
        })) {
            toast.error('Please select at least one recipient or subscriber', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        // Validate single recipient for direct messages (channel 5)
        const channelsArray = Array.isArray(channels) ? channels : [channels];
        if (channelsArray.includes("5") && selectedUserId.length !== 1) {
            toast.error('Direct messages can only be sent to one recipient at a time', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        const contactGroupObj: ContactGroupPayload = buildContactGroupPayload({
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
            isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected: props.isAllEmailOnlySubscribersSelected,
            categories,
            orgUuid: orgUuid || '',
            previewedRecipientData: previewedRecipientDataTemp,
        });
        
        // Add recipient count to the payload
        (contactGroupObj as any).recipientCount = previewedRecipientDataTemp.length;
    
        console.log('saveRecipientHandler - contactGroupObj:', contactGroupObj);
        console.log('saveRecipientHandler - previewedRecipientDataTemp:', previewedRecipientDataTemp);

        saveNotificationThreadConfig(contactGroupObj);
        onAction();
    };

    const [selectedFilterParams, setSelectedFilterParams] = useState<ContactGroupPayload | null>(null);

    /**
     * Converts recipient data format and moves to preview screen
     * Validates selection and builds payload for preview
     * For Direct Messages, checks if user has existing DM group and navigates directly if found
     * @returns {void}
     */
    const convertDataFormatToGetRecipientsAndMoveToNextScreen = (): void => {
        if (!isValidRecipientSelection({
            isAllSchoolLinkAppUsersSelected: props.isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected: props.isAllEmailOnlySubscribersSelected,
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
        })) {
            toast.error('Please select at least one recipient or subscriber', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        // Validate single recipient for direct messages (channel 5)
        const channelsArray = Array.isArray(channels) ? channels : [channels];
        if (channelsArray.includes("5") && selectedUserId.length !== 1) {
            toast.error('Direct messages can only be sent to one recipient at a time', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        // For Direct Messages, check if user has existing DM group and navigate directly
        if (channelsArray.includes("5") && selectedUserId.length === 1) {
            const selectedUser = users.find(u => u.uuid === selectedUserId[0]);
            if (selectedUser?.existingDmGroupUuid) {
                console.log('Navigating to existing DM thread:', selectedUser.existingDmGroupUuid);
                // Close the dialog
                onAction();
                // Navigate to the existing thread
                if (props.onNavigateToDmThread) {
                    props.onNavigateToDmThread(selectedUser.existingDmGroupUuid);
                }
                return;
            }
        }

        // Sync local state to parent props before moving to preview
        propsSetSelectedCategoryTags(selectedCategoryTags);
        setSelectedUsers(selectedUserId);
        propsSetSelectedEmailOnlySubscribers(selectedEmailOnlySubscribers);

        console.log('Moving to preview with selections:', {
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
            isAllSchoolLinkAppUsersSelected: props.isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected: props.isAllEmailOnlySubscribersSelected
        });

        const contactGroupObj: ContactGroupPayload = buildContactGroupPayload({
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
            isAllSchoolLinkAppUsersSelected: props.isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected: props.isAllEmailOnlySubscribersSelected,
            categories,
            orgUuid: orgUuid || '',
            epubOrgId,
            channels: props.channels,
        });

        setSelectedFilterParams(contactGroupObj);
        setIsNext(true);
    };

    /**
     * Updates an existing recipient group
     * Sends update request to server and refreshes group data
     * @returns {void}
     */
    const UpdateRecipientHandler = (): void => {
        const groupId = props.isRecipientUpdate;
        if (selectedUserId.length === 0 && selectedCategoryTags.length === 0 && !props.isRecipientUpdate) {
            toast.error('You have to select at least one recipient or subscriber', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
            return;
        }

        const finalData: ContactGroupPayload = buildContactGroupPayload({
            selectedUserId,
            selectedCategoryTags,
            selectedEmailOnlySubscribers,
            isAllSchoolLinkAppUsersSelected: props.isAllSchoolLinkAppUsersSelected,
            isAllEmailOnlySubscribersSelected: props.isAllEmailOnlySubscribersSelected,
            categories,
            orgUuid: orgUuid || '',
            channels: props.channels,
            previewedRecipientData: previewedRecipientDataTemp,
        });

        setIsSaving(true);
        updateContactGoup(finalData, groupId as string, authCtx?.token || '')
            .then((response) => {
                setIsSaving(false);
                
                // Update recipient data from server response - only if still on same thread
                if (response.data?.recipients && threadUuidRef.current === groupId) {
                    console.log('NotificationRecipientsSelectorDialog: Updating recipient data for thread:', groupId);
                    props.setPreviewedRecipientData(response.data.recipients);
                    props.setRecipientsCount(response.data.recipientCount || response.data.recipients.length);
                } else if (threadUuidRef.current !== groupId) {
                    console.log('NotificationRecipientsSelectorDialog: Ignoring stale recipient update for thread:', groupId, 'current thread:', threadUuidRef.current);
                }
                
                return getSelectUsersForGroup(groupId as string, authCtx?.token || '')
                    .then(groupResponse => {
                        // Only update if we're still on the same thread (prevent stale updates)
                        if (threadUuidRef.current === groupId) {
                            console.log('NotificationRecipientsSelectorDialog: Updating selectUsersForGroup for thread:', groupId);
                            props.setSelectUsersForGroup(groupResponse.data);
                        } else {
                            console.log('NotificationRecipientsSelectorDialog: Ignoring stale update for thread:', groupId, 'current thread:', threadUuidRef.current);
                        }
                    });
            })
            .then(() => {
                toast.success("Contact groups updated successfully!", {
                    position: toast.POSITION.BOTTOM_RIGHT,
                    type: 'success'
                });
                setOpen(false);
            })
            .catch((error: any) => {
                setIsSaving(false);
                console.error('Error updating contact group:', error);
                toast.error(
                    error?.message || 'Failed to update contact groups. Please try again.',
                    {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    }
                );
            });
    };

    let currentComponent: ReactElement | null = null;

    if (currentTab === 1) {
        currentComponent = (
            <SchoolLinkAppUsers
                categories={categories}
                users={users}
                onSelectTag={setSelectedTagsHandlers}
                selectedCategoryTags={selectedCategoryTags}
                isAllSchoolLinkAppUsersSelected={props.isAllSchoolLinkAppUsersSelected}
                setIsAllSchoolLinkAppUsersSelected={props.setIsAllSchoolLinkAppUsersSelected}
            />
        );
    } else if (currentTab === 2) {
        currentComponent = (
            <EmailOnlySubscribers
                appData={emailOnlySubscribers}
                selectedEmailOnlySubscribers={selectedEmailOnlySubscribers}
                onChange={emailOnlySubscribersChangeHandler}
                isAllEmailOnlySubscribersSelected={isAllEmailOnlySubscribersSelected}
                setIsAllEmailOnlySubscribersSelected={setIsAllEmailOnlySubscribersSelected}
                selectedChannels={channels}
            />
        );
    } else {
        currentComponent = (
            <IndividualAppUsers
                onCheckedUser={getUserCheckedValueHandler}
                users={users}
                channel={channels}
                selectedUserId={selectedUserId}
                isAllSchoolLinkAppUsersSelected={isAllSchoolLinkAppUsersSelected}
                setIsAllSchoolLinkAppUsersSelected={setIsAllSchoolLinkAppUsersSelected}
                onNavigateToDmThread={(groupUuid) => {
                    // Close the dialog
                    onAction();
                    // Navigate to the existing thread using the callback if provided
                    if (props.onNavigateToDmThread) {
                  
                        props.onNavigateToDmThread(groupUuid);
                    }
                }}
            />
        );
    }

    // Fetch Select contact groups data
    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            try {
                // Skip these API calls if channels contains "5"
                const channelsArray = Array.isArray(channels) ? channels : [channels];
                const shouldSkipApiCalls = channelsArray.includes("5");

                const [categoryResponse, emailOnlySubscribersResponse, appUserResponse] = await Promise.all([
                    shouldSkipApiCalls 
                        ? Promise.resolve({ data: [] }) 
                        : getCategoryTags(orgUuid || '', authCtx?.token || ''),
                    shouldSkipApiCalls || !epubOrgId
                        ? Promise.resolve({ data: [] }) 
                        : fetchEmailOnlySubscribers(epubOrgId || '', authCtx?.token || ''),
                    // Use fetchAppUsersForDM when channel 5 is selected to get DM group info
                    shouldSkipApiCalls
                        ? fetchAppUsersForDM(orgUuid || '', authCtx?.token || '')
                        : fetchAppUsers(orgUuid || '', authCtx?.token || ''),
                ]);

                if (isRecipientUpdate !== false) {
                    try {
                        const response = await getSelectedRecipientData(
                            isRecipientUpdate as string,
                            authCtx?.token || ''
                        );
                        setIsAllSchoolLinkAppUsersSelected(
                            stringToBool(response.isAllSchoolLinkAppUsersSelected)
                        );
                        setIsAllEmailOnlySubscribersSelected(
                            stringToBool(response.isAllEmailOnlySubscribersSelected)
                        );
                        
                        // Process userGroupTags to set selected tags per category
                        if (response.userGroupCategories && response.userGroupTags) {
                            const categoryTagsMap: any[] = [];
                            
                            // Group tags by category
                            response.userGroupCategories.forEach((cat: any) => {
                                const categoryTags = response.userGroupTags
                                    .filter((tag: any) => {
                                        // Find matching tags for this category from the full categories list
                                        const fullCategory = (categoryResponse || []).find((c: any) => c.id === cat.categoryID);
                                        return fullCategory && fullCategory.tags.some((t: any) => t.id === tag.tagId);
                                    })
                                    .map((tag: any) => ({
                                        label: tag.name,
                                        value: tag.tagId
                                    }));
                                
                                if (categoryTags.length > 0) {
                                    categoryTagsMap.push({
                                        [cat.categoryID]: categoryTags
                                    });
                                }
                            });
                            
                            setSelectedCategoryTags(categoryTagsMap);
                            propsSetSelectedCategoryTags(categoryTagsMap);
                        }
                        
                        // Set selected users
                        if (response.selectedUsers) {
                            const usersArray = response.selectedUsers.split(',').filter((u: string) => u.trim());
                            setSelectedUserId(usersArray);
                            setSelectedUsers(usersArray);
                        }
                        
                        // Set selected email only subscribers
                        if (response.emailOnlySubscribers && Array.isArray(response.emailOnlySubscribers)) {
                            try {
                                // emailOnlySubscribers format: [{"categoryId": [{label, value}, ...]}]
                                // We need to flatten it to match the component's expected format
                                const formattedSubscribers: EmailSubscriberSelection[] = [];
                                
                                response.emailOnlySubscribers.forEach((categoryObj: any) => {
                                    // Each categoryObj is like {"1158": [{label, value}, ...]}
                                    Object.entries(categoryObj).forEach(([categoryId, tags]) => {
                                        if (Array.isArray(tags)) {
                                            formattedSubscribers.push({
                                                [categoryId]: tags
                                            });
                                        }
                                    });
                                });
                                
                                setSelectedEmailOnlySubscribers(formattedSubscribers);
                                propsSetSelectedEmailOnlySubscribers(formattedSubscribers);
                            } catch (e) {
                                console.error('Error processing email only subscribers:', e);
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching selected recipient data:', error);
                    }
                }

                setCategories(categoryResponse || []);
                setEmailOnlySubscribers(emailOnlySubscribersResponse || []);
                setUsers(appUserResponse?.userList || []);
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                console.error('Error fetching notification recipient data:', error);
                toast.error('Failed to load contact groups. Please try again.', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgUuid, epubOrgId, authCtx?.token, channels, isRecipientUpdate]);

    // Sync props to local state on modal open only
    useEffect(() => {
        if (modalStatus) {
            const selectesUsers = selectedUsers || [];
            const selectedCategoriesTagsData = selectedCategoriesTags || [];
            const selectedEmailOnlySubscribersData = propsSelectedEmailOnlySubscribers || [];

            console.log('Syncing props to local state:', {
                selectesUsers,
                selectedCategoriesTagsData,
                selectedEmailOnlySubscribersData
            });

            setSelectedCategoryTags(selectedCategoriesTagsData);
            setSelectedEmailOnlySubscribers(selectedEmailOnlySubscribersData);
            setSelectedUserId(selectesUsers);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalStatus, selectedUsers, selectedCategoriesTags, propsSelectedEmailOnlySubscribers]);

    return (
        <>
            {isLoading && <Loader />}
            <Modal
                size="xl"
                show={isOpen}
                className="xlgModelSize"
                centered={true}
                onHide={props.onAction}
                aria-labelledby="example-modal-sizes-title-lg"
            >
                {!isNext ? (
                    <>
                        <Modal.Header>
                            <Modal.Title id="example-modal-sizes-title-lg" className={style.modalTitle} style={{flex:1}}>
                                <h4 className={tableStyle.modalTitle}>Select contact groups</h4>
                            </Modal.Title>

                            <div>
                                <button 
                                    type="button" 
                                    onClick={props.onAction} 
                                    className={`${button.modalBtn} ${button.borderButton} ${button.buttonSpaceLeft}`}
                                >
                                    Cancel
                                </button>

                                <button 
                                    type="button" 
                                    style={{ border: "1px solid" }} 
                                    disabled={isSaving} 
                                    onClick={convertDataFormatToGetRecipientsAndMoveToNextScreen} 
                                    className={`${button.modalBtn} ${button.whiteButton} ${button.buttonSpaceLeft}`}
                                >
                                    {isSaving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            {' '}Saving...
                                        </>
                                    ) : (
                                        'Next'
                                    )}
                                </button>
                            </div>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="row">
                                <div className={`col-md-5 ${style.leftGroup}`}>
                                    <div className={style['group-list']}>
                                        {!(Array.isArray(props.channels) ? props.channels : [props.channels]).includes("5") && (
                                            <>
                                                <button 
                                                    type="button" 
                                                    onClick={() => tabChangeHandler(1)} 
                                                    className={currentTab === 1 ? style.active : ''}
                                                >
                                                    <div className={style.displayinline}>
                                                        <div className={style.groupIcon}>
                                                            <ImMobile className={style.icon} />
                                                        </div>
                                                        <div className={style.groupDetail}>
                                                            <h5>SchoolLink app users</h5>
                                                            <p>
                                                                Select your preferred contact groups from the parents who have downloaded the app. You can communicate to these groups via multiple channels.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>

                                                <button 
                                                    type="button" 
                                                    onClick={() => tabChangeHandler(2)} 
                                                    className={currentTab === 2 ? style.active : ''}
                                                >
                                                    <div className={style.displayinline}>
                                                        <div className={style.groupIcon}>
                                                            <MdOutlineMarkunreadMailbox className={style.icon} />
                                                        </div>
                                                        <div className={style.groupDetail}>
                                                            <h5>Email only subscribers</h5>
                                                            <p>
                                                               <strong style={{fontFamily:'Roboto-Bold'}}> These are your current subscribers from ePublisher</strong>, typically uploaded via CSV or web form. Can be used only for email communications.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            </>
                                        )}
                                        {props.isAllSchoolLinkAppUsersSelected === false && (
                                            <button 
                                                type="button" 
                                                onClick={() => tabChangeHandler(3)} 
                                                className={currentTab === 3 ? style.active : ''}
                                            >
                                                <div className={style.displayinline}>
                                                    <div className={style.groupIcon}>
                                                        <FaUserPlus className={style.icon} />
                                                    </div>
                                                    <div className={style.groupDetail}>
                                                        <h5>Select individual app users</h5>
                                                        <p>
                                                            In addition to the contact groups, if you wish to include specific app users in the notification, you can select them here.
                                                        </p>
                                                    </div>
                                                </div>
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className={`col-md-7 ${style.rightGroup}`}>
                                    <div className={style.rightSide}>{currentComponent}</div>
                                </div>
                            </div>
                        </Modal.Body>
                    </>
                ) : (
                    <NotificationRecipientPreview
                        onAction={props.onAction}
                        saveRecipientHandler={saveRecipientHandler}
                        UpdateRecipientHandler={UpdateRecipientHandler}
                        isSaving={isSaving}
                        isNext={isNext}
                        setIsNext={setIsNext}
                        isOpen={isOpen}
                        setRecipientsCount={props.setRecipientsCount}
                        selectedParameters={selectedFilterParams}
                        isRecipientUpdate={props.isRecipientUpdate}
                        orgUuid={orgUuid || ''}
                        setPreviewedRecipientData={props.setPreviewedRecipientData}
                        setPreviewedRecipientDataTemp={setPreviewedRecipientDataTemp}
                    />
                )}
            </Modal>
        </>
    );
};

export default NotificationRecipientsSelectorDialog;
